import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { WidgetTheme } from '@intent/shared';
import type { ChatClient } from '../core/chat-client';
import { App } from './App';
import styles from './styles.css?inline';

/**
 * Resolves the effective colour scheme.
 *
 * `auto` follows the host page's preference, which is almost always what a
 * customer wants — a light widget pinned to a dark site looks broken.
 */
function resolveScheme(theme: WidgetTheme): 'light' | 'dark' {
  if (theme.colorScheme !== 'auto') return theme.colorScheme;

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Chooses black or white text for a background, by perceived luminance. */
function contrastingText(hex: string): string {
  const normalised = hex.replace('#', '');
  const red = parseInt(normalised.slice(0, 2), 16);
  const green = parseInt(normalised.slice(2, 4), 16);
  const blue = parseInt(normalised.slice(4, 6), 16);

  if ([red, green, blue].some(Number.isNaN)) return '#ffffff';

  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
  return luminance > 0.6 ? '#111111' : '#ffffff';
}

function themeVariables(theme: WidgetTheme): string {
  const scheme = resolveScheme(theme);
  const dark = scheme === 'dark';

  return `
    --ic-font: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    --ic-accent: ${theme.accentColor};
    --ic-accent-fg: ${contrastingText(theme.accentColor)};
    --ic-surface: ${dark ? '#1a1a1a' : '#ffffff'};
    --ic-bubble: ${dark ? '#242424' : '#f6f6f6'};
    --ic-fg: ${dark ? '#f2f2f2' : '#141414'};
    --ic-muted: ${dark ? '#9a9a9a' : '#6b6b6b'};
    --ic-border: ${dark ? '#2e2e2e' : '#e6e6e6'};
    --ic-online: ${dark ? '#3ecf8e' : '#16a34a'};
  `;
}

/**
 * Mounts the widget inside a shadow root.
 *
 * Shadow DOM is not a nicety here: the widget renders on arbitrary customer
 * sites, and without it their CSS reset would restyle our buttons while our
 * styles leaked onto their page. The boundary makes both impossible.
 */
export function mountWidget(client: ChatClient, theme: WidgetTheme): () => void {
  const host = document.createElement('div');
  host.id = 'intent-chat-widget';
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: 'open' });

  const styleElement = document.createElement('style');
  styleElement.textContent = `:host {${themeVariables(theme)}}\n${styles}`;
  shadow.appendChild(styleElement);

  const container = document.createElement('div');
  shadow.appendChild(container);

  const root: Root = createRoot(container);
  root.render(
    <StrictMode>
      <App client={client} theme={theme} />
    </StrictMode>,
  );

  return () => {
    root.unmount();
    host.remove();
  };
}
